import React, { useEffect, useState } from "react";
import axios from "axios";
import { getToken } from "../utils/auth";
import { Link } from "react-router-dom";
import DataTable from "react-data-table-component";

const ReturnAlerts = () => {
  const [orders, setOrders] = useState([]);
  const [deliverymen, setDeliverymen] = useState([]);
  const [selectedDeliveryman, setSelectedDeliveryman] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReturnAlerts();
    fetchDeliverymen();
  }, []);

  const fetchReturnAlerts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const res = await axios.get(
        "https://sunrise-backend.sunrisemeditechsystems.com/api/orders/return/alerts",
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOrders(res.data);
    } catch (error) {
      console.error("Error fetching return alerts:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDeliverymen = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get("https://sunrise-backend.sunrisemeditechsystems.com/api/staffusers", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDeliverymen(res.data.filter((u) => u.role === "deliveryman"));
    } catch (error) {
      console.error("Error fetching deliverymen:", error);
    }
  };

  const assignDeliveryman = async (orderId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `https://sunrise-backend.sunrisemeditechsystems.com/api/orders/${orderId}/assign`,
        { deliveryman_id: selectedDeliveryman[orderId] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Refresh the list after assignment
      fetchReturnAlerts();
    } catch (error) {
      alert("Failed to assign deliveryman.");
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      delivered: { class: "bg-success", text: "Delivered (Ready for Return)" },
      return_pending: { class: "bg-warning", text: "Return Pending" },
      out_for_return: { class: "bg-info", text: "Out for Return" },
    };
    const config = statusConfig[status] || {
      class: "bg-secondary",
      text: status,
    };
    return <span className={`badge ${config.class}`}>{config.text}</span>;
  };

  const customStyles = {
    headCells: {
      style: {
        backgroundColor: "#39465c",
        color: "white",
        fontWeight: "bold",
        fontSize: "15px",
      },
    },
    cells: {
      style: {
        padding: "8px",
        fontSize: "14px",
      },
    },
    rows: {
      style: {
        minHeight: "45px",
        "&:hover": {
          backgroundColor: "#f1f1f1",
        },
      },
    },
  };

  const columns = [
    {
      name: "Order #",
      selector: (row) => row.id,
      sortable: true,
      width: "140px",
    },
    {
      name: "Customer",
      cell: (row) =>
        row.source === "sunrise-website"
          ? `${row.shipping_address?.firstName || "Guest"} ${
              row.shipping_address?.lastName || ""
            }`
          : `${row.shipping_address?.doctorName || "Doctor"} - ${
              row.shipping_address?.hospitalName || "Hospital"
            }`,
      sortable: true,
      width: "250px",
    },
    {
      name: "Product",
      selector: (row) => {
        const items = Array.isArray(row.items) ? row.items : []; // ✅ ensures array
        const item = items.find((i) => i.buyOrRent === "rent");
        return item?.name ?? "-";
      },
      sortable: true,
      width: "300px",
    },
    {
      name: "Return Date",
      selector: (row) => {
        const items = Array.isArray(row.items) ? row.items : [];
        const item = items.find((i) => i.buyOrRent === "rent");
        return item?.rentalEndDate
          ? new Date(item.rentalEndDate).toLocaleDateString()
          : "-";
      },
      sortable: true,
      width: "300px",
    },
    {
      name: "Status",
      cell: (row) => getStatusBadge(row.status),
      sortable: true,
      width: "150px",
    },
    {
      name: "Assign for Return",
      cell: (row) => (
        <div className="d-flex align-items-center gap-8">
          {row.status === "delivered" ? (
            <>
              <select
                className="form-select form-select-sm d-inline-block w-auto"
                value={selectedDeliveryman[row.id] || ""}
                onChange={(e) =>
                  setSelectedDeliveryman({
                    ...selectedDeliveryman,
                    [row.id]: e.target.value,
                  })
                }
              >
                <option value="">Select Deliveryman</option>
                {deliverymen.map((dm) => (
                  <option key={dm.id} value={dm.id}>
                    {dm.username}
                  </option>
                ))}
              </select>
              <button
                className="btn btn-sm btn-success ms-2"
                disabled={!selectedDeliveryman[row.id]}
                onClick={() => assignDeliveryman(row.id)}
              >
                Assign
              </button>
            </>
          ) : (
            <span>
              {deliverymen.find((dm) => dm.id === row.assignedDeliveryman)
                ?.username || "Assigned"}
            </span>
          )}
        </div>
      ),
      width: "420px",
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  if (loading)
    return (
      <div className="text-center p-5">
        <div className="spinner-border"></div>
      </div>
    );

  return (
    <div className="content-page">
      <div className="content">
        <div className="container-fluid">
          {/* Page Title */}
          <div className="row">
            <div className="col-12">
              <div className="page-title-box">
                <div className="page-title-right">
                  <ol className="breadcrumb m-0">
                    <li className="breadcrumb-item">
                      <Link to="/">Home</Link>
                    </li>
                    <li className="breadcrumb-item active">Return Alerts</li>
                  </ol>
                </div>
                <h4 className="page-title">Return Alerts</h4>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              {orders.length === 0 ? (
                <div className="text-center py-5">
                  <i className="mdi mdi-bell-ring-outline mdi-48px text-muted"></i>
                  <h5 className="mt-3">No Pending Returns</h5>
                  <p className="text-muted">
                    There are no rental orders due for return today.
                  </p>
                </div>
              ) : (
                <div className="table-responsive">
                  {/* <table className="table table-centered table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Order #</th>
                        <th>Customer</th>
                        <th>Product</th>
                        <th>Return Date</th>
                        <th>Status</th>
                        <th>Assign for Return</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map(order => {
                        const item = order.items.find(i => i.buyOrRent === 'rent');
                        return (
                          <tr key={order._id}>
                            <td>{order._id.slice(-6).toUpperCase()}</td>
                            <td> {order.source === 'sunrise-website'
                              ? `${order.shippingAddress?.firstName || 'Guest'} ${order.shippingAddress?.lastName || ''}`
                              : `${order.shippingAddress?.doctorName || 'Doctor'} - ${order.shippingAddress?.hospitalName || 'Hospital'}`
                            }</td>
                            <td>{item?.name}</td>
                            <td>{item?.rentalEndDate ? new Date(item.rentalEndDate).toLocaleDateString() : '-'}</td>
                            <td>{getStatusBadge(order.status)}</td>
                            <td className='d-flex gap-12'>
                              {order.status === 'delivered' ? (
                                <>
                                  <select
                                    className="form-select form-select-sm d-inline-block w-auto"
                                    value={selectedDeliveryman[order._id] || ""}
                                    onChange={e =>
                                      setSelectedDeliveryman({ ...selectedDeliveryman, [order._id]: e.target.value })
                                    }
                                  >
                                    <option value="">Select Deliveryman</option>
                                    {deliverymen.map(dm => (
                                      <option key={dm._id} value={dm._id}>{dm.username}</option>
                                    ))}
                                  </select>
                                  <button
                                    className="btn btn-sm btn-success ms-2"
                                    disabled={!selectedDeliveryman[order._id]}
                                    onClick={() => assignDeliveryman(order._id)}
                                  >
                                    Assign
                                  </button>
                                </>
                              ) : (
                                <span>{deliverymen.find(dm => dm._id === order.assignedDeliveryman)?.username || "Assigned"}</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table> */}
                  <DataTable
                    // title="Return Orders"
                    columns={columns}
                    data={orders}
                    highlightOnHover
                    striped
                    responsive
                    pagination
                    customStyles={customStyles}
                    defaultSortFieldId="date" // sort by Date column
                    defaultSortAsc={false}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReturnAlerts;
